import { InternalRule, PermissionOptionItem, Rule } from './types'

// Generic function to update rules
const updateRules = (args: {
  internalRules: InternalRule[]
  permissionOptionsMap: Map<string, any>
  idxToUpdate: number
  updateType: 'action' | 'module'
  newValues: any
  onRulesChange: (rules: Rule[]) => void
  setInternalRules: (rules: InternalRule[]) => void
  setSelectedModuleValues: (values: any) => void
  actionProcessor?: (value: PermissionOptionItem[]) => string[]
}) => {
  const {
    internalRules,
    permissionOptionsMap,
    setSelectedModuleValues,
    onRulesChange,
    setInternalRules,
    idxToUpdate,
    updateType,
    newValues,
    actionProcessor
  } = args
  const updatedInternalRules = []
  const updatedExternalRules = []

  for (let i = 0; i < internalRules.length; i++) {
    const rule = internalRules[i]

    if (i === idxToUpdate) {
      // Update the rule at the target index
      let updatedInternalRule
      let updatedExternalRule

      if (updateType === 'action') {
        const wasAllSelected = rule.actions.some((action) => action.value === '$all')
        // Process all selected actions to get their sub-actions
        const allActions = new Set<string>()

        // Only process if not $all was selected before. If was selected then all will be removed
        if (!wasAllSelected) {
          // Below block is required when action's subaction is more than one, we need to maintain other subactions
          // eg: create: ['read', 'create', 'update'] -> if create is getting removed, update needs to be retained
          newValues.forEach((selectedAction) => {
            if (actionProcessor) {
              const subActions = actionProcessor([selectedAction])
              subActions.forEach((action) => allActions.add(action))
            }
          })

          // This is not required as read is already included in the subactions
          // if (allActions.size > 0 && !allActions.has('read')) {
          //   allActions.add('read')
          // }
        }

        const finalActionsArr = Array.from(allActions)

        updatedInternalRule = {
          ...rule,
          actions: convertActionsToInternal(finalActionsArr, permissionOptionsMap)
        }
        updatedExternalRule = convertRuleToExternalFormat(rule, finalActionsArr)
      } else {
        // module update

        // When module changes, remove the asset/folder properties,
        // hence the need to spread the rest of the properties
        const { assets, folders, ...rest } = rule
        updatedInternalRule = { ...rest, module: newValues }
        updatedExternalRule = convertRuleToExternalFormat(rest, undefined, newValues)

        // reset the respective idxToUpdate property in selectedModuleValues
        setSelectedModuleValues((prev) => {
          const { [idxToUpdate]: currentIdxValue, ...rest } = prev
          // here newValues will be object since it is module udpate
          const newModuleValue = { [idxToUpdate]: { [newValues.value === 'asset' ? 'assets' : 'folders']: [] } }
          return { ...rest, ...newModuleValue }
        })
      }

      updatedInternalRules.push(updatedInternalRule)
      updatedExternalRules.push(updatedExternalRule)
    } else {
      // Keep unchanged rules as-is for internal, convert for external
      updatedInternalRules.push(rule)
      updatedExternalRules.push(convertRuleToExternalFormat(rule))
    }
  }

  onRulesChange(updatedExternalRules)
  setInternalRules(updatedInternalRules)
}

// Function to convert internal rule to external format
const convertRuleToExternalFormat = (rule: any, actionsOverride?: string[], moduleOverride?: any) => {
  const { actions, ...rest } = rule
  const isLocale = rule.module === 'locale'
  const actionObj = {}

  if (actionsOverride) {
    // For updated rule with new actions
    actionsOverride.forEach((action) => {
      if (action !== '$all') {
        actionObj[action] = true
      }
    })
  } else {
    // For unchanged rules, convert existing actions
    rule.actions.forEach((action) => {
      if (action && action.value && action.value !== '$all') {
        actionObj[action.value] = true
      }
    })
  }

  return {
    ...rest,
    action: actionObj,
    ...(!isLocale ? { module: moduleOverride ? moduleOverride.value || '$all' : rule.module.value || '$all' } : {})
  }
}

// Function to convert actions to internal format (Permission objects)
const convertActionsToInternal = (actions: string[], permissionOptionsMap: Map<string, any>) => {
  return actions.map((action) => permissionOptionsMap.get(action)).filter(Boolean)
}

export { updateRules, convertRuleToExternalFormat, convertActionsToInternal }
